<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useCampaignWhatsappConnections } from 'dashboard/composables/useCampaignWhatsappConnections';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import EmailCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/EmailCampaign/EmailCampaignForm.vue';
import SMSCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SMSCampaign/SMSCampaignForm.vue';
import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const props = defineProps({
  audienceList: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const selectedChannel = ref('email');

const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const accountId = useMapGetter('getCurrentAccountId');
const emailInboxes = useMapGetter('inboxes/getEmailInboxes');
const smsInboxes = useMapGetter('inboxes/getSMSInboxes');
const whatsAppInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const hasCloudWhatsAppInbox = computed(() =>
  (whatsAppInboxes.value ?? []).some(
    inbox => inbox.provider === 'whatsapp_cloud'
  )
);

// A WhatsApp Web (Evolution) connection is a valid sender too — the form
// supports it and even auto-selects it when there is no Cloud inbox. Gating the
// channel on a Cloud inbox alone hid WhatsApp entirely from accounts that
// dispatch over a connected phone, which is the common setup here.
const { readyConnections, fetchConnections } = useCampaignWhatsappConnections();
const canSendWhatsApp = computed(
  () => hasCloudWhatsAppInbox.value || readyConnections.value.length > 0
);

onMounted(fetchConnections);

// A channel is offered only when the account can actually send on it: the
// feature flag is on AND there is a usable inbox. Offering a dead channel would
// only produce campaigns that fail at trigger time.
const channels = computed(() => [
  {
    value: 'email',
    label: t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.CHANNELS.EMAIL'),
    available:
      isFeatureEnabledOnAccount.value(
        accountId.value,
        FEATURE_FLAGS.EMAIL_CAMPAIGNS
      ) && (emailInboxes.value ?? []).length > 0,
    reachable: props.audienceList?.email_count ?? 0,
  },
  {
    value: 'sms',
    label: t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.CHANNELS.SMS'),
    available: (smsInboxes.value ?? []).length > 0,
    reachable: props.audienceList?.phone_count ?? 0,
  },
  {
    value: 'whatsapp',
    label: t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.CHANNELS.WHATSAPP'),
    available:
      isFeatureEnabledOnAccount.value(
        accountId.value,
        FEATURE_FLAGS.WHATSAPP_CAMPAIGNS
      ) && canSendWhatsApp.value,
    reachable: props.audienceList?.phone_count ?? 0,
  },
]);

const availableChannels = computed(() =>
  channels.value.filter(channel => channel.available)
);

watch(availableChannels, list => {
  if (!list.some(channel => channel.value === selectedChannel.value)) {
    selectedChannel.value = list[0]?.value ?? 'email';
  }
});

const activeChannel = computed(() =>
  channels.value.find(channel => channel.value === selectedChannel.value)
);

const successKeyByChannel = {
  email: 'CAMPAIGN.EMAIL.CREATE.FORM.API.SUCCESS_MESSAGE',
  sms: 'CAMPAIGN.SMS.CREATE.FORM.API.SUCCESS_MESSAGE',
  whatsapp: 'CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE',
};

const handleSubmit = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);
    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });
    useAlert(t(successKeyByChannel[selectedChannel.value]));
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.API.ERROR_MESSAGE')
    );
  }
};

const handleCancel = () => dialogRef.value?.close();

const open = () => {
  selectedChannel.value = availableChannels.value[0]?.value ?? 'email';
  dialogRef.value?.open();
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.TITLE', { name: audienceList?.name })
    "
    :description="t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <div class="flex flex-col gap-5">
      <div
        v-if="!availableChannels.length"
        class="py-6 text-sm text-center text-n-slate-11"
      >
        {{ t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.NO_CHANNELS') }}
      </div>

      <template v-else>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.CHANNEL_LABEL') }}
          </span>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="channel in availableChannels"
              :key="channel.value"
              type="button"
              class="flex flex-col items-start gap-0.5 px-4 py-2 rounded-lg border transition-colors"
              :class="
                selectedChannel === channel.value
                  ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="selectedChannel = channel.value"
            >
              <span class="text-sm font-medium">{{ channel.label }}</span>
              <span class="text-xs">
                {{
                  t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.REACHABLE', {
                    count: channel.reachable,
                  })
                }}
              </span>
            </button>
          </div>
          <p
            v-if="activeChannel && activeChannel.reachable === 0"
            class="text-xs text-n-ruby-11"
          >
            {{ t('CAMPAIGN.AUDIENCE.NEW_CAMPAIGN.NO_REACHABLE_CONTACTS') }}
          </p>
        </div>

        <EmailCampaignForm
          v-if="selectedChannel === 'email'"
          :preset-audience-list-id="audienceList?.id"
          @submit="handleSubmit"
          @cancel="handleCancel"
        />
        <SMSCampaignForm
          v-else-if="selectedChannel === 'sms'"
          :preset-audience-list-id="audienceList?.id"
          @submit="handleSubmit"
          @cancel="handleCancel"
        />
        <WhatsAppCampaignForm
          v-else
          :preset-audience-list-id="audienceList?.id"
          @submit="handleSubmit"
          @cancel="handleCancel"
        />
      </template>
    </div>
  </Dialog>
</template>
